import { useState } from 'react'
import { Badge, Card, DataState, PageHeader } from '@/components/Common'
import { Field, FormSection, TextField } from '@/components/Form'
import { ConfirmDialog, Modal } from '@/components/Modal'
import { useAuth } from '@/lib/auth'
import { useResource } from '@/lib/resource'
import {
  ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  api,
  type AppUserRow,
  type Role,
} from '@/lib/api'

export function Users() {
  const users = useResource<AppUserRow>('/api/v1/users')
  const { user: me } = useAuth()

  const [adding, setAdding] = useState(false)
  const [resetting, setResetting] = useState<AppUserRow | null>(null)
  const [deleting, setDeleting] = useState<AppUserRow | null>(null)

  const list = users.data ?? []

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Who may sign in to this console, and what they may change."
        actions={
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            Add user
          </button>
        }
      />

      {users.saveError && <div className="notice notice-error">{users.saveError}</div>}

      <Card>
        <p className="card-note">
          Roles are cumulative. The split that matters is between{' '}
          <strong>worklist administrator</strong> and{' '}
          <strong>configuration administrator</strong>: editing a worklist
          changes what a device sees, while editing a device changes which
          device it is — and on a multi-site installation that is a privacy
          boundary rather than a preference.
        </p>

        <DataState
          loading={users.loading}
          error={users.error}
          empty={list.length === 0}
          emptyMessage="No accounts."
        />

        {list.length > 0 && (
          <table className="ae-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Role</th>
                <th>Last signed in</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id} className={row.enabled ? undefined : 'row-muted'}>
                  <td className="mono">
                    {row.username}
                    {row.username === me?.username && <span className="tag"> you</span>}
                  </td>
                  <td>{row.display_name || '—'}</td>
                  <td>
                    <select
                      value={row.role}
                      disabled={users.saving || row.username === me?.username}
                      onChange={(e) =>
                        void users.update(row.id, { role: e.target.value as Role })
                      }
                    >
                      {ROLES.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </option>
                      ))}
                    </select>
                    {!row.enabled && <> <Badge state="DISABLED" /></>}
                    {row.must_change_password && (
                      <div className="hint">must set a new password</div>
                    )}
                  </td>
                  <td className="hint">{row.last_login_at || 'never'}</td>
                  <td className="actions">
                    <button
                      className="btn btn-small"
                      disabled={users.saving || row.username === me?.username}
                      onClick={() => void users.update(row.id, { enabled: !row.enabled })}
                    >
                      {row.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button className="btn btn-small" onClick={() => setResetting(row)}>
                      Reset password
                    </button>
                    <button
                      className="btn btn-small btn-danger-quiet"
                      disabled={row.username === me?.username}
                      onClick={() => setDeleting(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="table-footnote">
          You cannot change your own role, disable your own account, or remove
          the last enabled security administrator — the way back from any of
          those is a DBA writing SQL.
        </p>
      </Card>

      {adding && (
        <AddUser
          saving={users.saving}
          error={users.saveError}
          onClose={() => {
            setAdding(false)
            users.clearError()
          }}
          onSave={async (draft) => {
            if (await users.create(draft)) setAdding(false)
          }}
        />
      )}

      {resetting && (
        <ResetPassword
          user={resetting}
          onClose={() => {
            setResetting(null)
            users.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete '${deleting.username}'?`}
          confirmLabel="Delete user"
          destructive
          busy={users.saving}
          error={users.saveError}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            if (await users.remove(deleting.id)) setDeleting(null)
          }}
        >
          <p>Their sessions end immediately.</p>
          <p className="hint">
            Disabling keeps the account and the audit trail intelligible.
          </p>
        </ConfirmDialog>
      )}
    </>
  )
}

function AddUser({
  saving,
  error,
  onClose,
  onSave,
}: {
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: Record<string, unknown>) => void
}) {
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<Role>('viewer')

  return (
    <Modal
      title="Add user"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving || !username || password.length < 12}
            onClick={() =>
              onSave({ username, display_name: displayName, password, role })
            }
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}
      <FormSection title="Account">
        <div className="form-grid">
          <TextField label="Username" value={username} onChange={setUsername} required />
          <TextField label="Display name" value={displayName} onChange={setDisplayName} />
        </div>
        <TextField
          label="Temporary password"
          value={password}
          onChange={setPassword}
          type="password"
          hint="At least 12 characters. They must change it at first sign-in, and can do nothing else until they have."
        />
        <Field label="Role">
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          <span className="field-hint">{ROLE_DESCRIPTIONS[role]}</span>
        </Field>
      </FormSection>
    </Modal>
  )
}

function ResetPassword({ user, onClose }: { user: AppUserRow; onClose: () => void }) {
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/v1/users/${user.id}/password`, { new_password: password })
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`Reset password for ${user.username}`}
      onClose={onClose}
      footer={
        done ? (
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={busy || password.length < 12}
              onClick={() => void submit()}
            >
              {busy ? 'Setting…' : 'Set password'}
            </button>
          </>
        )
      }
    >
      {error && <div className="notice notice-error">{error}</div>}
      {done ? (
        <div className="notice notice-ok">
          Password set. Every session that account had has ended, and they must
          choose a new password at their next sign-in.
        </div>
      ) : (
        <>
          <TextField
            label="Temporary password"
            value={password}
            onChange={setPassword}
            type="password"
            hint="At least 12 characters."
          />
          <p className="hint">
            They will be required to change it immediately — an administrator who
            knows an operator's password is a shared credential. All their
            existing sessions end, which is the point when this is being used in
            response to a compromise.
          </p>
        </>
      )}
    </Modal>
  )
}

export function UsersDenied() {
  return (
    <div className="notice notice-warn">
      <h1>Not permitted</h1>
      <p>Managing console accounts needs the security administrator role.</p>
    </div>
  )
}
